import React from 'react';
import { pricingPlans } from '../data/mockData';
import { Button } from './ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const PricingCard = ({ plan, onOpenContactForm, delay }) => {
  const card = useScrollAnimation({ threshold: 0.15 });

  return (
    <div
      ref={card.ref}
      data-testid={`pricing-card-${plan.id}`}
      className={`group relative rounded-xl p-8 flex flex-col scroll-fade-up scroll-delay-${delay} ${card.isVisible ? 'scroll-visible' : ''} ${
        plan.recommended
          ? 'bg-[#101423] border-2 border-amber-500/50 scale-[1.03] shadow-[0_0_60px_rgba(245,158,11,0.15)] glow-amber'
          : 'bg-white/[0.02] card-hover-premium card-glow-border'
      }`}
    >
      {plan.recommended && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-amber-500 to-orange-600 text-white text-[10px] font-bold uppercase tracking-wider px-5 py-1.5 rounded-md">
            Most Popular
          </span>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-0.5 text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{plan.name}</h3>
        <p className="text-xs text-slate-500 mb-3">{plan.tagline}</p>
        <div className="flex items-baseline gap-1">
          <span className={`text-5xl font-black tracking-tighter ${plan.recommended ? 'text-gradient-amber' : 'text-white'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>{plan.price}</span>
        </div>
        <p className="text-xs mt-1 text-slate-500">{plan.federal}</p>
      </div>

      <ul className="space-y-3 flex-1 mb-6">
        {plan.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-2.5">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.recommended ? 'text-amber-500' : 'text-indigo-400/60'}`} />
            <span className="text-sm text-slate-400">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        <p className="text-[10px] text-center mb-3 text-slate-600">{plan.note}</p>
        <Button
          data-testid={`pricing-cta-${plan.id}`}
          onClick={onOpenContactForm}
          className={`w-full font-bold h-11 rounded-md uppercase tracking-wide text-xs ${
            plan.recommended
              ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:from-amber-600 hover:to-orange-700 btn-shine shadow-lg shadow-amber-500/20'
              : 'bg-white/10 border border-white/20 text-white hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:border-indigo-500'
          }`}
        >
          {plan.buttonText}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const PricingSection = ({ onOpenContactForm }) => {
  const header = useScrollAnimation({ threshold: 0.2 });

  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-[#0A0A12] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div ref={header.ref} className={`text-center max-w-2xl mx-auto mb-16 scroll-fade-up ${header.isVisible ? 'scroll-visible' : ''}`}>
          <span className="text-xs tracking-[0.2em] uppercase text-amber-500 mb-3 block font-bold">Pricing</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-white mb-4 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
            No Hidden Fees.<br /><span className="text-gradient-amber">No Surprises.</span>
          </h2>
          <p className="text-base text-slate-300 leading-relaxed">
            Flat-rate pricing from $45. You know exactly what you pay before you commit. Every plan includes a licensed trademark attorney.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
          {pricingPlans.map((plan, i) => (
            <PricingCard key={plan.id} plan={plan} onOpenContactForm={onOpenContactForm} delay={i + 1} />
          ))}
        </div>

        <p className="text-center text-sm text-slate-500 mt-10 max-w-2xl mx-auto">
          <strong className="text-amber-500/80">Important:</strong> All packages exclude USPTO federal filing fees ($250-$350 per class), paid directly to the government.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
